import type { AccountInfo } from '@azure/msal-browser';

/**
 * Reading role and scope out of the Entra token.
 *
 * Two different claims, answering two different questions, and the story
 * requires both to be surfaced:
 *
 *   ROLE  — *what actions* are permitted. Delivered in the `roles` claim as
 *           the app roles assigned on the CLM app registration.
 *   SCOPE — *which parties* are visible. Delivered in the `groups` claim as
 *           the Entra security groups the user belongs to (practice / client
 *           scope groups, plus functional groups such as CEQ-Legal).
 *
 * NFR-06 — nothing read here is an authorisation decision. The gateway API
 * re-derives role and scope from the bearer token on every request and applies
 * scope as a query predicate. What this module produces is display material
 * and usability affordances. A user who edits their own token cache changes
 * what their nav rail looks like and nothing else.
 *
 * NFR-05 also wants the *effective* access visible to the user, and this is
 * only an approximation of it: group object ids are not group names, and Entra
 * truncates the claim past a threshold. Both gaps are handled below and both
 * are resolved properly by the API in a later story (PR-11 owns role x scope
 * resolution and the `vw_effective_access` view).
 */

/**
 * Claims read off the id token. Field names are snake_case to match the token
 * and the API contract (CLAUDE.md rule 12); React components and hooks stay
 * camelCase as the language expects.
 */
export interface ClmIdentity {
  readonly display_name: string | null;
  readonly username: string | null;
  /** Entra object id of the user (`oid`). The audit actor key. */
  readonly object_id: string | null;
  readonly tenant_id: string | null;
  /** App roles from the `roles` claim — what actions are permitted. */
  readonly roles: readonly string[];
  /**
   * CLM-65 — roles the gateway WOULD derive from the `groups` claim, named as
   * the API names them (`admin`, `contributor`, `reader`, `legal_reviewer`),
   * for each configured `VITE_ENTRA_GROUP_CLM_*` object id present in the
   * claim. cloudEQ grants CLM roles through security groups, not app roles, so
   * a member of CEQ-CLM-Admin holds no `roles` claim at all and the badge read
   * "No role claim" on the first production sign-in. Display only, like
   * everything here (NFR-06): the API re-derives the role on every call and
   * `permissions.ts` never consults this list. Empty when no group id is
   * configured (D-65-2).
   */
  readonly roles_from_groups: readonly string[];
  /**
   * Group claim values — every group in the token, of which the CLM scope
   * groups (CEQ-CLM-Scope-*) decide which parties are visible. The name is
   * historical: role groups and unrelated groups are in here too, and the SPA
   * cannot tell a scope group from the rest (permissions.ts `scopeConfidence`).
   * Usually group object ids (GUIDs) unless the app registration is configured
   * to emit sAMAccountName for synced groups, so treat these as opaque handles,
   * not labels.
   */
  readonly scope_groups: readonly string[];
  /**
   * True when Entra omitted the group list because it exceeded the claim size
   * limit (`hasgroups` / `_claim_names` are present instead). The groups are
   * then only retrievable from Graph or, in our case, from the API — so the
   * SPA must say "resolved server-side" rather than "no scope", which would be
   * an actively misleading readout on the most privileged users.
   */
  readonly scope_truncated: boolean;
}

export const ANONYMOUS_IDENTITY: ClmIdentity = {
  display_name: null,
  username: null,
  object_id: null,
  tenant_id: null,
  roles: [],
  roles_from_groups: [],
  scope_groups: [],
  scope_truncated: false,
};

/**
 * The role names the gateway uses in its responses — `packages/auth/src/config.ts`
 * `roleGroups` keys, with `legal` spelled `legal_reviewer` as the API spells it.
 * Enumerated here for DISPLAY: `permissions.ts` still treats roles as opaque
 * strings and gates nothing on these.
 */
export type ClmRoleName = 'admin' | 'contributor' | 'reader' | 'legal_reviewer';

/** One configured `VITE_ENTRA_GROUP_CLM_*` value: which group grants which role. */
export interface RoleGroupBinding {
  readonly role: ClmRoleName;
  /** Entra security-group object id, as the `groups` claim carries it. */
  readonly group_id: string;
}

/**
 * Role names derived from the `groups` claim, in binding order, de-duplicated.
 * Object ids are compared case-insensitively — Entra emits them lower-case but
 * a hand-typed repository variable may not be.
 */
export function rolesFromGroups(
  groups: readonly string[],
  bindings: readonly RoleGroupBinding[],
): readonly string[] {
  const held = new Set(groups.map((group) => group.toLowerCase()));
  const roles: string[] = [];
  for (const binding of bindings) {
    if (held.has(binding.group_id.toLowerCase()) && !roles.includes(binding.role)) {
      roles.push(binding.role);
    }
  }
  return roles;
}

/**
 * What the identity footer shows as role tags (CLM-65, D-65-2).
 *
 *   tags      — app-role claims first, unchanged, then group-derived roles the
 *               claims did not already name (case-insensitive de-duplication).
 *   fallback  — when there is nothing to tag:
 *               'resolved_by_api' — no group id is configured, so the SPA cannot
 *                                   name a role and says so rather than "none";
 *               'no_role_group'   — ids are configured, none matched, and the
 *                                   token carries no app role either: a real
 *                                   NFR-05 group-hygiene condition.
 *               Never an error state — the API decides what the user can do.
 */
export type RoleTagFallback = 'resolved_by_api' | 'no_role_group';

export function roleTags(
  identity: Pick<ClmIdentity, 'roles' | 'roles_from_groups'>,
  roleGroupsConfigured: boolean,
): { readonly tags: readonly string[]; readonly fallback: RoleTagFallback | null } {
  const tags: string[] = [...identity.roles];
  const seen = new Set(tags.map((role) => role.toLowerCase()));
  for (const role of identity.roles_from_groups) {
    if (!seen.has(role.toLowerCase())) {
      seen.add(role.toLowerCase());
      tags.push(role);
    }
  }
  if (tags.length > 0) return { tags, fallback: null };
  return { tags, fallback: roleGroupsConfigured ? 'no_role_group' : 'resolved_by_api' };
}

function asStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function readIdentity(
  account: AccountInfo | null,
  roleGroups: readonly RoleGroupBinding[] = [],
): ClmIdentity {
  if (!account) return ANONYMOUS_IDENTITY;

  const claims: Record<string, unknown> = account.idTokenClaims ?? {};

  // Entra signals group overage in one of two ways depending on the token
  // version and whether the optional claim is configured; check both.
  const scope_truncated =
    claims['hasgroups'] === true ||
    claims['hasgroups'] === 'true' ||
    typeof claims['_claim_names'] === 'object';

  const scope_groups = asStringArray(claims['groups']);

  return {
    display_name: asString(claims['name']) ?? account.name ?? null,
    username: asString(claims['preferred_username']) ?? account.username ?? null,
    object_id: asString(claims['oid']) ?? account.localAccountId ?? null,
    tenant_id: asString(claims['tid']) ?? account.tenantId ?? null,
    roles: asStringArray(claims['roles']),
    roles_from_groups: rolesFromGroups(scope_groups, roleGroups),
    scope_groups,
    scope_truncated,
  };
}
